import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

import Image from '../../../components/AppImage';

const DealDetailsModal = ({ deal, onClose, onUpdateStage, loading }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const stages = [
    'inquiry',
    'initial_contact',
    'negotiation',
    'due_diligence',
    'contract_finalization',
    'completed'
  ];

  const handleStageUpdate = (newStage) => {
    onUpdateStage(deal?.id, newStage);
  };

  const formatDate = (date) => {
    if (!date) return 'Not set';
    return new Date(date)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {deal?.player?.profilePhoto && (
              <Image
                src={deal?.player?.profilePhoto}
                alt={`${deal?.player?.fullName} profile photo`}
                className="w-16 h-16 rounded-full object-cover"
              />
            )}
            <div>
              <h2 className="text-2xl font-bold text-foreground">{deal?.dealTitle}</h2>
              <p className="text-sm text-muted-foreground">{deal?.player?.fullName} - {deal?.player?.primaryPosition}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex gap-4 px-6">
            {['overview', 'milestones', 'timeline']?.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 px-2 border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab?.charAt(0)?.toUpperCase() + tab?.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Current Stage */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Current Stage</h3>
                <div className="flex flex-wrap gap-2">
                  {stages?.map(stage => (
                    <button
                      key={stage}
                      onClick={() => handleStageUpdate(stage)}
                      disabled={loading}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        deal?.currentStage === stage
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {stage?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </button>
                  ))}
                </div>
              </div>

              {/* Deal Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="text-sm text-muted-foreground mb-1">Proposed Fee</div>
                  <div className="text-2xl font-bold text-success">€{deal?.proposedFee}M</div>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="text-sm text-muted-foreground mb-1">Priority</div>
                  <div className="text-2xl font-bold text-foreground capitalize">{deal?.priorityLevel}</div>
                </div>
              </div>

              {/* Parties */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Parties Involved</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Building" size={16} color="var(--color-primary)" />
                      <span className="text-sm font-medium text-foreground">Club</span>
                    </div>
                    <div className="text-sm text-muted-foreground">{deal?.club?.organization || deal?.club?.full_name}</div>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="User" size={16} color="var(--color-accent)" />
                      <span className="text-sm font-medium text-foreground">Representative</span>
                    </div>
                    <div className="text-sm text-muted-foreground">{deal?.playerRepresentative?.full_name}</div>
                  </div>
                </div>
              </div>

              {/* Next Action */}
              {deal?.nextAction && (
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircle" size={20} color="var(--color-primary)" />
                    <div className="flex-1">
                      <div className="font-medium text-foreground mb-1">Next Action</div>
                      <div className="text-sm text-muted-foreground mb-2">{deal?.nextAction}</div>
                      {deal?.nextDeadline && (
                        <div className="text-xs text-muted-foreground">
                          Deadline: {formatDate(deal?.nextDeadline)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Contract Terms */}
              {deal?.contractTerms && Object?.keys(deal?.contractTerms)?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Contract Terms</h3>
                  <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                    {Object?.entries(deal?.contractTerms)?.map(([key, value]) => (
                      <div key={key} className="flex justify-between text-sm">
                        <span className="text-muted-foreground capitalize">{key?.replace('_', ' ')}</span>
                        <span className="text-foreground font-medium">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'milestones' && (
            <div className="space-y-3">
              {deal?.milestones?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No milestones defined for this deal
                </div>
              ) : (
                deal?.milestones?.map((milestone, index) => (
                  <div
                    key={index}
                    className={`border rounded-lg p-4 ${
                      milestone?.completed
                        ? 'border-success bg-success/5' :'border-border bg-card'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        milestone?.completed ? 'bg-success' : 'bg-muted'
                      }`}>
                        {milestone?.completed && <Icon name="Check" size={14} color="white" />}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-foreground capitalize">{milestone?.stage?.replace('_', ' ')}</div>
                        {milestone?.date && (
                          <div className="text-sm text-muted-foreground mt-1">
                            {formatDate(milestone?.date)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="text-center py-8 text-muted-foreground">
              Timeline view coming soon
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealDetailsModal;